import { HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { EMPTY } from 'rxjs';
import { OnboardingStatusService } from '../features/onboarding/services/onboarding-status.service';

/**
 * Routes that are always allowed regardless of onboarding status.
 * Mirrors the backend PendingStatusMiddleware's allowed-list so that
 * blocked requests never reach the server (avoiding 403 toasts).
 */
const ALLOWED_URL_PATTERNS = [
  '/assets/',
  'assets/',
  '/config',
  '/api/auth/',
  '/api/onboarding',
  '/api/user-onboarding',
];

/**
 * Silently blocks HTTP requests that are not relevant during onboarding.
 *
 * Requests are blocked (returned as EMPTY) when either:
 * 1. The company status is 'Pending' or 'Invited' (company not yet onboarded), or
 * 2. The individual user status is known but not yet 'Active' (e.g. user has
 *    not completed personal KYC or is awaiting approval).
 *
 * In both cases, only auth and onboarding endpoints are allowed through.
 * Silently completing blocked requests prevents unnecessary 403 responses
 * from the backend and the resulting error toasts.
 */
export const pendingStatusInterceptor: HttpInterceptorFn = (req, next) => {
  const onboardingStatusService = inject(OnboardingStatusService);

  const companyStatus = onboardingStatusService.getStatus();
  const userStatus = onboardingStatusService.userStatus();

  // Gate when the company is in a pre-onboarding state
  const isCompanyBlocked = companyStatus === 'Pending' || companyStatus === 'Invited';

  // Gate when the individual user is known but not yet Active (null means status
  // not yet loaded — let those requests through to avoid blocking on cold starts)
  const isUserBlocked = userStatus !== null && userStatus !== 'Active';

  if (!isCompanyBlocked && !isUserBlocked) {
    return next(req);
  }

  // Allow requests that match the permitted URL patterns
  const url = req.url;
  const isAllowed = ALLOWED_URL_PATTERNS.some((pattern) => url.includes(pattern));

  if (isAllowed) {
    return next(req);
  }

  // Silently complete — no error emitted, no toast triggered
  return EMPTY;
};
